'use strict';

/**
 * GenericStore implementation using the IndexedDB APIs in order to persist data
 * with methods for generic CRUD operations.
 *
 * Will automatically handle necessary upgrades of the IndexedDB database.
 */

(function() {
    window.atex = window.atex || {};

    atex.GenericStore = function(q, config) {
        var CONFIG_DATABASE_NAME_PARAM = 'databaseName';
        var CONFIG_DATABASE_VERSION_PARAM = 'databaseVersion';

        var DEFAULT_CONFIG = {};

        DEFAULT_CONFIG[CONFIG_DATABASE_NAME_PARAM] = 'atex.onecms.act';

        var FIXED_CONFIG = {};

        FIXED_CONFIG[CONFIG_DATABASE_VERSION_PARAM] = 3;

        var configuration = _.extend(DEFAULT_CONFIG, config, FIXED_CONFIG);

        var createError = function(errorObject) {
            return atex.onecms.error.Error(_.extend(errorObject, { origin: 'atex.GenericStore' }));
        };

        var createAndHandleError = function(message, exception, errorFn) {
            errorFn = errorFn || function() {};

            console.error(message, exception);

            var e = createError({
                type: atex.onecms.error.types.STORAGE_ERROR,
                cause: message,
                rootCause: exception
            });

            errorFn(e);
        };

        /**
         * Preserves the 'this' pointer when using the reject method on a deferred object
         */
        var rejector = function(deferred) {
            return deferred.reject.bind(deferred);
        };

        /**
         * Attempt to open the ART content object database. The given error callback will
         * be used for all kinds of errors, including any such occurring for the database
         * open operation itself.
         *
         * This method will also automatically handle any triggered 'upgradeneeded' event
         * for the database.
         *
         * NOT PROMISE BASED, because mostly used internally in file.
         */
        var openDatabase = function(success, error) {
            success = success || function() {};
            error = error || function() {};

            var request = undefined;

            try {
                if (config && config.indexDB) {
                    request = config.indexDB.open(configuration[CONFIG_DATABASE_NAME_PARAM], configuration[CONFIG_DATABASE_VERSION_PARAM]);
                } else {
                    request = window.indexedDB.open(configuration[CONFIG_DATABASE_NAME_PARAM], configuration[CONFIG_DATABASE_VERSION_PARAM]);
                }
            } catch (e) {
                createAndHandleError('Error while opening IndexedDB database!', e, error);

                return;
            }

            request.onerror = function(event) {
                createAndHandleError('Error while opening IndexedDB database!', event.target.error, error);
            };

            request.onsuccess = function(event) {
                var database = event.target.result;

                /**
                 * We try to handle all errors on the database level.
                 * Since the default behavior of errors for transactions
                 * are to bubble all the way up and then abort, global
                 * error handling should be good enough for now.
                 */

                database.onerror = function(event) {
                    database.close();

                    createAndHandleError('Error during IndexedDB database operation!', event.target.error, error);
                };

                database.onversionchange = function(event) {
                    /**
                     * Receiving an 'onversionchange' event at this point most likely means our
                     * application in a different page has requested a version change. We always
                     * try to play nice with application that wants to upgrade the database and
                     * thus close the database connection.
                     */

                    database.close();

                    createAndHandleError('IndexedDB database version change requested during GenericStore operation!', event.target.error, error);
                };

                try {
                    success(database);
                } catch (e) {
                    database.close();
                    createAndHandleError('Exception caught when calling success function!', e, error);
                }
            };

            request.onupgradeneeded = atex.upgrade.dbUpgradeCallback;
        };

        var clearAndInitializeDatabase = function() {
            var deferred = q.defer();

            var deleteRequest = undefined;

            try {
                deleteRequest = window.indexedDB.deleteDatabase(configuration[CONFIG_DATABASE_NAME_PARAM]);
            } catch (e) {
                createAndHandleError('Error while deleting IndexedDB database!', e, rejector(deferred));
            }

            deleteRequest.onerror = function(event) {
                createAndHandleError('Error while deleting IndexedDB database!', event.target.error, rejector(deferred));
            };

            deleteRequest.onblocked = function(event) {
                console.warn('IndexedDB database deletion request blocked, waiting for go-ahead...');
            };

            deleteRequest.onsuccess = function(event) {
                openDatabase(function(database) {
                    database.close();

                    deferred.resolve();
                }, function(err) {
                    createAndHandleError('Error while re-creating IndexedDB database!', err, rejector(deferred));
                });
            };

            return deferred.promise;
        };

        /**
         * NOT PROMISE BASED, because used before we have a promise library.
         */
        var touchDatabase = function(success, error) {
            success = success || function() {};
            error = error || function() {};

            openDatabase(function(database) {
                database.close();

                success();
            }, function(err) {
                createAndHandleError('Error while touching (opening) IndexedDB database!', err, error);
            });
        };

        return {
            getInternals: function() {
                return {
                    openDatabase: openDatabase,
                    clearAndInitializeDatabase: clearAndInitializeDatabase,
                    touch: touchDatabase
                };
            },

            getAll: function(storeName) {
                var deferred = q.defer();

                openDatabase(function(database) {
                    var results = [];

                    var transaction = database.transaction([storeName]);
                    var store = transaction.objectStore(storeName);

                    var request = store.openCursor();

                    request.onsuccess = function(event) {
                        var cursor = event.target.result;

                        if (cursor) {
                            results.push(cursor.value);
                            cursor.continue();
                        } else {
                            database.close();

                            deferred.resolve(results);
                        }
                    };
                }, function(err) {
                    createAndHandleError('Error while retrieving data from GenericStore!', err, rejector(deferred));
                });

                return deferred.promise;
            },

            getById: function(storeName, id) {
                var deferred = q.defer();

                openDatabase(function(database) {
                    var transaction = database.transaction([storeName]);
                    var store = transaction.objectStore(storeName);

                    var request = store.get(id);

                    request.onsuccess = function(event) {

                        var result = null;

                        if (event.target.result) {
                            result = event.target.result;
                        }

                        database.close();

                        deferred.resolve(result);
                    };
                }, function(err) {
                    createAndHandleError('Error while retrieving data from GenericStore!', err, rejector(deferred));
                });

                return deferred.promise;
            },

            deleteById: function(storeName, id) {
                var deferred = q.defer();

                openDatabase(function(database) {
                    var transaction = database.transaction([storeName], "readwrite");
                    var store = transaction.objectStore(storeName);

                    var request = store.delete(id);

                    request.onsuccess = function(event) {
                        database.close();

                        deferred.resolve();
                    };
                }, function(err) {
                    createAndHandleError('Error while deleting data from GenericStore!', err, rejector(deferred));
                });

                return deferred.promise;
            },

            put: function(storeName, obj) {
                var deferred = q.defer();

                openDatabase(function(database) {
                    var transaction = database.transaction([storeName], "readwrite");
                    var store = transaction.objectStore(storeName);
                    var request = store.put(obj);

                    request.onsuccess = function(event) {
                        database.close();

                        deferred.resolve(obj);
                    };
                }, function(err) {
                    createAndHandleError('Error while saving data to GenericStore!', err, rejector(deferred));
                });

                return deferred.promise;
            }
        };
    };
})();